(() => {
  const gallerySelector = '[data-product-gallery]';
  const cellSelector = '[data-product-gallery-media]';
  const wrapperSelector = '.product-gallery__video[data-html5-video]';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const videoState = new WeakMap();
  const galleryObservers = new WeakMap();
  const interactiveWrappers = new WeakSet();

  function getVideoState(video) {
    let state = videoState.get(video);
    if (!state) {
      state = {
        active: false,
        intersectionRatio: 0,
        userPaused: false,
        manualPlay: false
      };
      videoState.set(video, state);
    }
    return state;
  }

  function enforceNoControls(video) {
    if (!video) return;
    video.controls = false;
    video.removeAttribute('controls');
  }

  function updateInteractionLabel(wrapper, video) {
    if (!wrapper || !video) return;
    wrapper.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
  }

  function safePlay(video) {
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }

  function bindVideoInteraction(wrapper, video) {
    if (!wrapper || !video || interactiveWrappers.has(wrapper)) return;

    wrapper.setAttribute('role', 'button');
    wrapper.setAttribute('tabindex', '0');
    updateInteractionLabel(wrapper, video);

    const togglePlayback = () => {
      if (!isVideoActive(wrapper)) return;

      const state = getVideoState(video);

      if (video.paused || video.ended) {
        if (reduceMotion.matches) {
          state.manualPlay = true;
        } else {
          state.userPaused = false;
        }
        safePlay(video);
      } else {
        if (reduceMotion.matches) {
          state.manualPlay = false;
        } else {
          state.userPaused = true;
        }
        video.pause();
      }

      updateInteractionLabel(wrapper, video);
    };

    wrapper.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        togglePlayback();
      },
      true
    );

    wrapper.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      togglePlayback();
    });

    video.addEventListener('play', () => updateInteractionLabel(wrapper, video));
    video.addEventListener('pause', () => updateInteractionLabel(wrapper, video));

    interactiveWrappers.add(wrapper);
  }

  function configureVideo(wrapper) {
    if (!wrapper) return null;

    const video = wrapper.querySelector('video');
    if (!video) return null;

    video.defaultMuted = true;
    video.muted = true;
    video.volume = 0;
    video.playsInline = true;
    video.preload = 'metadata';
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('preload', 'metadata');
    enforceNoControls(video);

    const state = getVideoState(video);

    if (reduceMotion.matches) {
      video.loop = false;
      video.removeAttribute('loop');
    } else {
      video.loop = true;
      video.setAttribute('loop', '');
    }

    if (!state.controlObserver) {
      const controlObserver = new MutationObserver(() => enforceNoControls(video));
      controlObserver.observe(video, { attributes: true, attributeFilter: ['controls'] });
      state.controlObserver = controlObserver;
    }

    bindVideoInteraction(wrapper, video);
    return video;
  }

  function isVideoActive(wrapper) {
    const cell = wrapper.closest(cellSelector);
    if (!cell) return false;

    const gallery = cell.closest(gallerySelector);
    if (!gallery) return false;

    const selectedCell = gallery.querySelector(`${cellSelector}.is-selected`);
    if (selectedCell) return selectedCell === cell;

    const video = wrapper.querySelector('video');
    const state = video && videoState.get(video);
    return Boolean(state && state.intersectionRatio >= 0.75);
  }

  function styleVideoThumbnailBadges(gallery) {
    if (!gallery) return;

    const galleryWrapper = gallery.closest('[data-product-gallery-wrapper]');
    const thumbnails = galleryWrapper && galleryWrapper.querySelector('[data-product-gallery-thumbnails]');
    if (!thumbnails) return;

    const videoMediaIds = Array.from(
      gallery.querySelectorAll(`${cellSelector}[data-media-type="video"]`)
    )
      .map((cell) => cell.getAttribute('data-media-id'))
      .filter(Boolean);

    videoMediaIds.forEach((mediaId) => {
      const thumbnailCell = Array.from(
        thumbnails.querySelectorAll('.gallery-cell[data-media-id]')
      ).find((cell) => cell.getAttribute('data-media-id') === mediaId);

      const badge = thumbnailCell && thumbnailCell.querySelector('.media-badge');
      if (!badge || badge.classList.contains('artsif-video-media-badge')) return;

      badge.classList.add('artsif-video-media-badge');
      badge.setAttribute('viewBox', '0 0 48 48');
      badge.setAttribute('width', '48');
      badge.setAttribute('height', '48');
      badge.setAttribute('aria-hidden', 'true');
      badge.setAttribute('focusable', 'false');
      badge.innerHTML = `
        <circle cx="24" cy="24" r="22" fill="#FDF4EF" stroke="#EAD0AD" stroke-width="1.5"></circle>
        <path d="M20 16.5L33 24L20 31.5V16.5Z" fill="#8A4135"></path>
      `;
    });
  }

  function setVideoActive(wrapper, shouldPlay) {
    const video = configureVideo(wrapper);
    if (!video) return;

    const state = getVideoState(video);
    const active = shouldPlay && !document.hidden;

    if (reduceMotion.matches) {
      if (active && state.manualPlay) {
        safePlay(video);
      } else {
        video.pause();
      }

      if (!active) {
        if (state.active && !document.hidden) {
          try { video.currentTime = 0; } catch (error) {}
        }
        state.manualPlay = false;
      }

      state.active = active;
      updateInteractionLabel(wrapper, video);
      return;
    }

    if (active) {
      if (state.userPaused) {
        video.pause();
      } else {
        if (!state.active && video.currentTime > 0.15) {
          try { video.currentTime = 0; } catch (error) {}
        }
        safePlay(video);
      }
    } else {
      video.pause();
      if (state.active && !document.hidden) {
        try { video.currentTime = 0; } catch (error) {}
      }
      state.userPaused = false;
    }

    state.active = active;
    updateInteractionLabel(wrapper, video);
  }

  function updateGallery(gallery) {
    if (!gallery) return;

    styleVideoThumbnailBadges(gallery);

    gallery.querySelectorAll(wrapperSelector).forEach((wrapper) => {
      setVideoActive(wrapper, isVideoActive(wrapper));
    });
  }

  function initGallery(gallery) {
    if (!gallery || galleryObservers.has(gallery)) return;

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = configureVideo(entry.target);
          if (!video) return;
          const state = getVideoState(video);
          state.intersectionRatio = entry.intersectionRatio;
        });
        updateGallery(gallery);
      },
      { threshold: [0, 0.25, 0.75, 1] }
    );

    const observeVideos = () => {
      gallery.querySelectorAll(wrapperSelector).forEach((wrapper) => {
        configureVideo(wrapper);
        intersectionObserver.observe(wrapper);
      });
      styleVideoThumbnailBadges(gallery);
    };

    observeVideos();
    window.setTimeout(() => styleVideoThumbnailBadges(gallery), 0);
    window.setTimeout(() => styleVideoThumbnailBadges(gallery), 250);

    const mutationObserver = new MutationObserver(() => {
      observeVideos();
      updateGallery(gallery);
    });

    mutationObserver.observe(gallery, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class']
    });

    galleryObservers.set(gallery, { intersectionObserver, mutationObserver });
    updateGallery(gallery);
  }

  function initAll(root = document) {
    if (root.matches && root.matches(gallerySelector)) initGallery(root);
    if (root.querySelectorAll) root.querySelectorAll(gallerySelector).forEach(initGallery);
  }

  initAll();
  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll(gallerySelector).forEach(updateGallery);
  });

  const handleMotionPreferenceChange = () => {
    document.querySelectorAll(gallerySelector).forEach((gallery) => {
      gallery.querySelectorAll(wrapperSelector).forEach((wrapper) => {
        const video = configureVideo(wrapper);
        if (!video) return;
        const state = getVideoState(video);
        state.userPaused = false;
        state.manualPlay = false;
      });
      updateGallery(gallery);
    });
  };

  if (typeof reduceMotion.addEventListener === 'function') {
    reduceMotion.addEventListener('change', handleMotionPreferenceChange);
  } else if (typeof reduceMotion.addListener === 'function') {
    reduceMotion.addListener(handleMotionPreferenceChange);
  }
})();
